import api from '@/services/api';
import type {
  CreateTemplateInput,
  CustomiseTemplateInput,
  DocumentTemplateDetail,
  DocumentTemplateListParams,
  DocumentTemplateListResult,
  DocumentTemplateSummary,
  DocumentType,
  DocumentVariable,
  PaginationMeta,
  PreviewSource,
  SaveDraftInput,
  TemplateMetadataInput,
  TemplateOwner,
  TemplateSource,
  TemplatePreview,
  TemplateVersion,
  TemplateVersionStatus,
  TemplateVersionSummary,
  VersionHistoryResult,
} from '../types/documentTemplates.types';

const templatesPath = '/documents/templates';
const collectionPath = `${templatesPath}/`;
const toTemplateUrl = (id: string): string => `${templatesPath}/${id}`;

interface VersionSummaryDto {
  id: string;
  document_template_id: string;
  version_number: number;
  status: TemplateVersionStatus;
  revision: number;
  change_note: string | null;
  published_by_name: string | null;
  published_at: string | null;
  created_by_name: string | null;
  updated_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface VersionDto extends VersionSummaryDto {
  body_html: string;
}

interface SourceDto {
  template_id: string;
  template_name: string | null;
  version_id: string | null;
  version_number: number | null;
  newer_version_available: boolean;
}

interface TemplateSummaryDto {
  id: string;
  dealer_id: string | null;
  owner: TemplateOwner;
  key: string;
  name: string;
  description: string | null;
  document_type: DocumentType;
  published: VersionSummaryDto | null;
  has_draft: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface TemplateDetailDto extends TemplateSummaryDto {
  draft: VersionDto | null;
  source: SourceDto | null;
}

interface MetaDto {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface ListResponseDto {
  data: TemplateSummaryDto[];
  meta: MetaDto;
}

const mapMeta = ({ current_page, last_page, per_page, total }: MetaDto): PaginationMeta => ({
  currentPage: current_page,
  lastPage: last_page,
  perPage: per_page,
  total,
  from: total === 0 ? 0 : (current_page - 1) * per_page + 1,
  to: Math.min(current_page * per_page, total),
});

const mapVersionSummary = (dto: VersionSummaryDto): TemplateVersionSummary => ({
  id: dto.id,
  templateId: dto.document_template_id,
  versionNumber: dto.version_number,
  status: dto.status,
  revision: dto.revision,
  changeNote: dto.change_note,
  publishedByName: dto.published_by_name,
  publishedAt: dto.published_at,
  createdByName: dto.created_by_name,
  updatedByName: dto.updated_by_name,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

const mapVersion = (dto: VersionDto): TemplateVersion => ({
  ...mapVersionSummary(dto),
  bodyHtml: dto.body_html,
});

const mapSummary = (dto: TemplateSummaryDto): DocumentTemplateSummary => ({
  id: dto.id,
  key: dto.key,
  name: dto.name,
  description: dto.description,
  documentType: dto.document_type,
  owner: dto.owner,
  ownerDealerId: dto.dealer_id,
  published: dto.published ? mapVersionSummary(dto.published) : null,
  hasDraft: dto.has_draft,
  createdAt: dto.created_at,
  updatedAt: dto.updated_at,
});

const mapSource = (dto: SourceDto): TemplateSource => ({
  templateId: dto.template_id,
  templateName: dto.template_name,
  versionId: dto.version_id,
  versionNumber: dto.version_number,
  newerVersionAvailable: dto.newer_version_available,
});

const mapDetail = (dto: TemplateDetailDto): DocumentTemplateDetail => ({
  ...mapSummary(dto),
  draft: dto.draft ? mapVersion(dto.draft) : null,
  source: dto.source ? mapSource(dto.source) : null,
});

const toMetadataPayload = (input: TemplateMetadataInput) => ({
  name: input.name,
  description: input.description || null,
  document_type: input.documentType,
});

export const fetchDocumentTemplates = async (
  params: DocumentTemplateListParams = {},
): Promise<DocumentTemplateListResult> => {
  const response = await api.get<ListResponseDto>(collectionPath, {
    params: {
      search: params.search,
      document_type: params.documentType,
      status: params.status,
      owner: params.owner,
      dealer_id: params.dealerId,
      available_to_dealer_id: params.availableToDealerId,
      sort: params.sort,
      direction: params.direction,
      page: params.page,
      per_page: params.perPage,
    },
  });
  return { templates: response.data.data.map(mapSummary), meta: mapMeta(response.data.meta) };
};

export const fetchDocumentTemplate = async (id: string): Promise<DocumentTemplateDetail> => {
  const response = await api.get<{ data: TemplateDetailDto }>(toTemplateUrl(id));
  return mapDetail(response.data.data);
};

export const createDocumentTemplate = async (input: CreateTemplateInput): Promise<DocumentTemplateDetail> => {
  const response = await api.post<{ data: TemplateDetailDto }>(collectionPath, {
    dealer_id: input.dealerId,
    key: input.key,
    ...toMetadataPayload(input),
  });
  return mapDetail(response.data.data);
};

export const customiseDocumentTemplate = async (
  id: string,
  input: CustomiseTemplateInput,
): Promise<DocumentTemplateDetail> => {
  const response = await api.post<{ data: TemplateDetailDto }>(`${toTemplateUrl(id)}/customise`, {
    dealer_id: input.dealerId,
    key: input.key,
    name: input.name,
  });
  return mapDetail(response.data.data);
};

export const updateDocumentTemplate = async (
  id: string,
  input: TemplateMetadataInput,
): Promise<DocumentTemplateDetail> => {
  const response = await api.put<{ data: TemplateDetailDto }>(toTemplateUrl(id), toMetadataPayload(input));
  return mapDetail(response.data.data);
};

export const startDraft = async (id: string, fromVersionId?: string): Promise<TemplateVersion> => {
  const response = await api.post<{ data: VersionDto }>(
    `${toTemplateUrl(id)}/draft`,
    fromVersionId ? { from_version_id: fromVersionId } : {},
  );
  return mapVersion(response.data.data);
};

export const saveDraft = async (id: string, input: SaveDraftInput): Promise<TemplateVersion> => {
  const response = await api.put<{ data: VersionDto }>(`${toTemplateUrl(id)}/draft`, {
    body_html: input.bodyHtml,
    change_note: input.changeNote || null,
    revision: input.revision,
  });
  return mapVersion(response.data.data);
};

export const fetchDocumentVariables = async (): Promise<DocumentVariable[]> => {
  const response = await api.get<{ data: DocumentVariable[] }>('/documents/variables');
  return response.data.data;
};

export const deleteDocumentTemplate = async (id: string): Promise<void> => {
  await api.delete(toTemplateUrl(id));
};

export const discardDraft = async (id: string): Promise<void> => {
  await api.delete(`${toTemplateUrl(id)}/draft`);
};

export const publishDraft = async (id: string, revision: number): Promise<TemplateVersion> => {
  const response = await api.post<{ data: VersionDto }>(`${toTemplateUrl(id)}/draft/publish`, { revision });
  return mapVersion(response.data.data);
};

export const fetchTemplateVersions = async (
  id: string,
  page: number,
  perPage: number,
): Promise<VersionHistoryResult> => {
  const response = await api.get<{ data: VersionSummaryDto[]; meta: MetaDto }>(`${toTemplateUrl(id)}/versions`, {
    params: { page, per_page: perPage },
  });
  return { versions: response.data.data.map(mapVersionSummary), meta: mapMeta(response.data.meta) };
};

export const fetchTemplateVersion = async (id: string, versionId: string): Promise<TemplateVersion> => {
  const response = await api.get<{ data: VersionDto }>(`${toTemplateUrl(id)}/versions/${versionId}`);
  return mapVersion(response.data.data);
};

export const previewTemplate = async (id: string, source: PreviewSource): Promise<TemplatePreview> => {
  const payload = 'versionId' in source ? { version_id: source.versionId } : { body_html: source.bodyHtml };
  const response = await api.post<{
    data: { html: string; used_variables: string[]; empty_variables: string[] };
  }>(`${toTemplateUrl(id)}/preview`, payload);
  const { html, used_variables, empty_variables } = response.data.data;
  return { html, usedVariables: used_variables, emptyVariables: empty_variables };
};
